import React, { useState, useEffect } from "react";
import MultipleEmailForm from "./MultipleEmailForm";
import { MultiSelect } from "react-multi-select-component";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { base_url } from "../../Global";
import { render } from "@react-email/render";
import {
  collection,
  getDocs,
  addDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db, loginAdmin } from "../../Firebase";
import {
  Html,
  Head,
  Body,
  Container,
  Section,
  Heading,
  Text,
  Button,
  Preview,
  Img,
  Hr,
  Row,
  Column,
} from "@react-email/components";

const LegacyXTemplate = ({ blocks, globalSettings }) => {
  const main = {
    backgroundColor: globalSettings.pageColor,
    fontFamily: globalSettings.fontFamily,
  };
  const container = {
    backgroundColor: globalSettings.containerColor,
    margin: "0 auto",
    padding: "0px 0 0px",
    borderRadius: "12px",
    maxWidth: `${globalSettings.containerWidth || 600}px`,
    border: `1px solid ${globalSettings.borderColor}`,
    overflow: "hidden",
  };
  const styles = {
    heading: { lineHeight: "1.2" },
    text: { lineHeight: "1.5", whiteSpace: "pre-wrap" },
    image: {
      width: "100%",
      objectFit: "cover",
      backgroundColor: "#2a0a4a",
      display: "block",
    },
    button: {
      borderRadius: "6px",
      textDecoration: "none",
      display: "inline-block",
      padding: "12px 24px",
    },
    divider: {
      borderTop: `1px solid ${globalSettings.borderColor}`,
      width: "100%",
    },
  };

  const getSectionStyle = (block) => {
    let paddingObj = { padding: "0px", margin: "0px" };
    if (!(block.type === "image" && block.isHero)) {
      const pt = block.paddingTop !== undefined ? block.paddingTop : 0;
      const pb = block.paddingBottom !== undefined ? block.paddingBottom : 0;
      paddingObj = { padding: `${pt}px 32px ${pb}px 32px`, margin: "0px" };
    }
    let backgroundObj = {};
    if (block.useBgImage && block.bgImage) {
      backgroundObj = {
        backgroundImage: `url(${block.bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundColor: block.fallbackBgColor || "transparent",
      };
    } else if (block.fallbackBgColor) {
      backgroundObj = { backgroundColor: block.fallbackBgColor };
    }
    return { ...paddingObj, ...backgroundObj };
  };

  const renderTextContent = (text) => {
    if (!text) return null;
    return text.split("\n").map((line, i, arr) => (
      <span key={i}>
        {line}
        {i !== arr.length - 1 && <br />}
      </span>
    ));
  };

  const renderInnerElement = (element, isColumn = false) => {
    if (!element || element.type === "empty") return null;

    const align =
      element.align ||
      (["image", "button", "social"].includes(element.type)
        ? "center"
        : "left");

    const mTop = element.marginTop || 0;
    const mBot = element.marginBottom || 0;
    const mLeft = element.marginLeft || 0;
    const mRight = element.marginRight || 0;

    const wrapperStyle = {
      width: "100%",
      textAlign: align,
      paddingTop: `${mTop}px`,
      paddingBottom: `${mBot}px`,
      paddingLeft: `${mLeft}px`,
      paddingRight: `${mRight}px`,
      boxSizing: "border-box",
    };

    const innerPaddingStyle = {
      paddingTop: `${element.innerPaddingTop || 0}px`,
      paddingBottom: `${element.innerPaddingBottom || 0}px`,
      paddingLeft: `${element.innerPaddingLeft || 0}px`,
      paddingRight: `${element.innerPaddingRight || 0}px`,
    };

    if (element.type === "heading") {
      return (
        <div style={wrapperStyle}>
          <Heading
            style={{
              ...styles.heading,
              margin: 0,
              textAlign: align,
              color: element.color || globalSettings.headingColor,
              fontSize: element.fontSize ? `${element.fontSize}px` : "24px",
              fontWeight: element.isBold !== false ? "bold" : "normal",
              fontStyle: element.isItalic ? "italic" : "normal",
              textDecoration: element.isUnderline ? "underline" : "none",
              ...innerPaddingStyle,
            }}
          >
            {renderTextContent(element.text)}
          </Heading>
        </div>
      );
    }
    if (element.type === "text") {
      return (
        <div style={wrapperStyle}>
          <Text
            style={{
              ...styles.text,
              margin: 0,
              textAlign: align,
              color: element.color || globalSettings.textColor,
              fontSize: element.fontSize ? `${element.fontSize}px` : "15px",
              fontWeight: element.isBold ? "bold" : "normal",
              fontStyle: element.isItalic ? "italic" : "normal",
              textDecoration: element.isUnderline ? "underline" : "none",
              ...innerPaddingStyle,
            }}
          >
            {renderTextContent(element.text)}
          </Text>
        </div>
      );
    }
    if (element.type === "image") {
      const widthVal =
        element.width && parseInt(element.width) > 0
          ? parseInt(element.width)
          : undefined;
      return (
        <div style={wrapperStyle}>
          <div style={{ display: "inline-block", ...innerPaddingStyle }}>
            <Img
              src={
                element.src ||
                "https://via.placeholder.com/600x250/2a0a4a/ffffff?text=Image"
              }
              width={widthVal}
              alt="Content"
              style={{
                ...styles.image,
                width: widthVal ? `${widthVal}px` : "100%",
                maxWidth: "100%",
                display: "block",
                borderRadius: element.isHero && !isColumn ? "0" : "8px",
              }}
            />
          </div>
        </div>
      );
    }
    if (element.type === "button") {
      const gradStart = element.gradientStart || "#a855f7";
      const gradEnd = element.gradientEnd || "#4f46e5";
      const btnBackground = element.useGradient
        ? `linear-gradient(to right, ${gradStart}, ${gradEnd})`
        : element.bgColor || "#a855f7";
      return (
        <div style={wrapperStyle}>
          <Button
            style={{
              ...styles.button,
              margin: 0,
              background: btnBackground,
              backgroundColor: element.bgColor || "#a855f7",
              color: element.color || "#ffffff",
              fontSize: element.fontSize ? `${element.fontSize}px` : "16px",
              fontWeight: element.isBold !== false ? "bold" : "normal",
              fontStyle: element.isItalic ? "italic" : "normal",
              textDecoration: element.isUnderline ? "underline" : "none",
              ...innerPaddingStyle,
            }}
            href={element.url || "https://legacyx.uk"}
          >
            {element.text}
          </Button>
        </div>
      );
    }
    if (element.type === "social") {
      return (
        <div style={wrapperStyle}>
          <div style={{ display: "inline-block", ...innerPaddingStyle }}>
            {element.socials &&
              element.socials.map((social) => (
                <a
                  key={social.id}
                  href={social.url || "#"}
                  style={{
                    display: "inline-block",
                    margin: "0 8px",
                    textDecoration: "none",
                  }}
                >
                  <Img
                    src={social.iconUrl}
                    width={element.iconSize || 24}
                    height={element.iconSize || 24}
                    alt={social.platform}
                    style={{ display: "block" }}
                  />
                </a>
              ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Html>
      <Head />
      <Preview>{globalSettings.previewText || "Update"}</Preview>
      <Body style={main}>
        <Container style={container}>
          {blocks.map((block) => {
            if (block.type === "divider") {
              return (
                <Section key={block.id} style={getSectionStyle(block)}>
                  <Hr
                    style={{
                      ...styles.divider,
                      marginTop:
                        block.marginTop !== undefined
                          ? `${block.marginTop}px`
                          : "0px",
                      marginBottom:
                        block.marginBottom !== undefined
                          ? `${block.marginBottom}px`
                          : "0px",
                    }}
                  />
                </Section>
              );
            }
            if (block.type === "split") {
              return (
                <Section key={block.id} style={getSectionStyle(block)}>
                  <Row>
                    <Column
                      style={{
                        width: "50%",
                        paddingRight: "8px",
                        verticalAlign: "top",
                      }}
                    >
                      {renderInnerElement(block.left, true)}
                    </Column>
                    <Column
                      style={{
                        width: "50%",
                        paddingLeft: "8px",
                        verticalAlign: "top",
                      }}
                    >
                      {renderInnerElement(block.right, true)}
                    </Column>
                  </Row>
                </Section>
              );
            }
            return (
              <Section key={block.id} style={getSectionStyle(block)}>
                {renderInnerElement(block)}
              </Section>
            );
          })}
        </Container>
      </Body>
    </Html>
  );
};

const SOCIAL_PLATFORMS = [
  {
    value: "facebook",
    label: "Facebook",
    defaultIcon: "https://img.icons8.com/ios-filled/50/ffffff/facebook-new.png",
  },
  {
    value: "instagram",
    label: "Instagram",
    defaultIcon:
      "https://img.icons8.com/ios-filled/50/ffffff/instagram-new.png",
  },
  {
    value: "x",
    label: "X (Twitter)",
    defaultIcon: "https://img.icons8.com/ios-filled/50/ffffff/twitterx--v1.png",
  },
  {
    value: "linkedin",
    label: "LinkedIn",
    defaultIcon: "https://img.icons8.com/ios-filled/50/ffffff/linkedin.png",
  },
  {
    value: "youtube",
    label: "YouTube",
    defaultIcon: "https://img.icons8.com/ios-filled/50/ffffff/youtube-play.png",
  },
  {
    value: "tiktok",
    label: "TikTok",
    defaultIcon: "https://img.icons8.com/ios-filled/50/ffffff/tiktok.png",
  },
  {
    value: "custom",
    label: "Custom Icon",
    defaultIcon: "https://img.icons8.com/ios-filled/50/ffffff/link.png",
  },
];

const CampaignBuilder = ({ appName }) => {
  const [builderMode, setBuilderMode] = useState("advanced");
  const [users, setUsers] = useState([]);
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationText, setNotificationText] = useState("");
  const [isLoading, setLoading] = useState(false);
  const [selectFromMS, setselectFromMS] = useState([]);
  const [extraEmail, setExtraEmail] = useState([]);
  const [liveHtmlPreview, setLiveHtmlPreview] = useState("");
  const [uploadingBlockId, setUploadingBlockId] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [loadedTemplateId, setLoadedTemplateId] = useState(null);

  const [globalSettings, setGlobalSettings] = useState({
    pageColor: "#000000",
    containerColor: "#0a0a0a",
    borderColor: "#1f1f1f",
    fontFamily: "'Inter', sans-serif",
    headingColor: "#ffffff",
    textColor: "#a3a3a3",
    containerWidth: 600,
    previewText: `${appName.toUpperCase()} Update`,
  });

  const [blocks, setBlocks] = useState([
    {
      id: 1,
      type: "image",
      src: "https://via.placeholder.com/600x250/2a0a4a/ffffff?text=Header",
      width: 0,
      isHero: true,
      align: "center",
      paddingTop: 0,
      paddingBottom: 0,
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
      innerPaddingTop: 0,
      innerPaddingBottom: 0,
      innerPaddingLeft: 0,
      innerPaddingRight: 0,
    },
    {
      id: 2,
      type: "heading",
      text: `Step into ${appName.toUpperCase()}`,
      align: "left",
      color: "#ffffff",
      paddingTop: 32,
      paddingBottom: 8,
      fontSize: 24,
      isBold: true,
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
      innerPaddingTop: 0,
      innerPaddingBottom: 0,
      innerPaddingLeft: 0,
      innerPaddingRight: 0,
    },
    {
      id: 3,
      type: "text",
      text: "Designed for modern connection.",
      align: "left",
      color: "#a3a3a3",
      paddingTop: 0,
      paddingBottom: 16,
      fontSize: 15,
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
      innerPaddingTop: 0,
      innerPaddingBottom: 0,
      innerPaddingLeft: 0,
      innerPaddingRight: 0,
    },
    {
      id: 4,
      type: "divider",
      paddingTop: 16,
      paddingBottom: 16,
      marginTop: 0,
      marginBottom: 0,
    },
    {
      id: 5,
      type: "button",
      text: "Open App",
      url: "https://legacyx.uk",
      align: "center",
      bgColor: "#a855f7",
      color: "#ffffff",
      paddingTop: 16,
      paddingBottom: 32,
      fontSize: 16,
      isBold: true,
      useGradient: true,
      gradientStart: "#a855f7",
      gradientEnd: "#4f46e5",
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
      innerPaddingTop: 12,
      innerPaddingBottom: 12,
      innerPaddingLeft: 24,
      innerPaddingRight: 24,
    },
  ]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (appName === "lxdc") {
          const response = await fetch(base_url + "/auth/allProfile");
          if (!response.ok) throw new Error("Failed to fetch LXDC users");
          const data = await response.json();
          setUsers(
            data?.users
              ?.filter((u) => !!u.username?.trim())
              ?.map((u) => ({
                value: u._id,
                label: u.username,
                email: u.email,
              })) || [],
          );
        } else if (appName === "vortex") {
          await loginAdmin();
          const userSnapshot = await getDocs(collection(db, "users"));
          const usersList = userSnapshot.docs.map((doc) => ({
            id: doc.id,
            uid: doc.data().uid || doc.id,
            ...doc.data(),
          }));
          setUsers(
            usersList
              ?.filter((u) => !!u.name?.trim())
              ?.map((u) => ({
                value: u.id || u.uid,
                label: u.name,
                email: u.email,
              })) || [],
          );
        }
      } catch (error) {
        console.error(error);
      }
    };
    const fetchTemplates = async () => {
      try {
        const snapshot = await getDocs(collection(db, "emailTemplates"));
        setSavedTemplates(
          snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
        );
      } catch (error) {
        console.error(error);
      }
    };
    fetchUsers();
    fetchTemplates();
  }, [appName]);

  useEffect(() => {
    if (builderMode === "advanced") {
      const compileEmail = async () => {
        try {
          const htmlString = await render(
            <LegacyXTemplate blocks={blocks} globalSettings={globalSettings} />,
            { pretty: true },
          );
          setLiveHtmlPreview(htmlString);
        } catch (error) {
          console.error(error);
        }
      };
      compileEmail();
    }
  }, [blocks, globalSettings, builderMode]);

  const handleNotificationTitle = (e) => {
    const v = e.target.value;
    setNotificationTitle(v.charAt(0).toUpperCase() + v.slice(1));
  };
  const handleNotificationTextChange = (e) => {
    const v = e.target.value;
    setNotificationText(v.charAt(0).toUpperCase() + v.slice(1));
  };
  const removeEmail = (i) =>
    setExtraEmail(extraEmail.filter((_, index) => index !== i));
  const AddEmail = (data) =>
    setExtraEmail([...extraEmail, { email: data?.Enteremail }]);

  const cleanBlocksForStorage = (currentBlocks) =>
    currentBlocks.map((block) => {
      let cb = JSON.parse(JSON.stringify(block));
      if (cb.type === "image" && cb.src?.startsWith("data:image")) cb.src = "";
      if (cb.useBgImage && cb.bgImage?.startsWith("data:image"))
        cb.bgImage = "";
      if (cb.type === "split") {
        if (cb.left.type === "image" && cb.left.src?.startsWith("data:image"))
          cb.left.src = "";
        if (cb.right.type === "image" && cb.right.src?.startsWith("data:image"))
          cb.right.src = "";
        if (cb.left.bgImage?.startsWith("data:image")) cb.left.bgImage = "";
        if (cb.right.bgImage?.startsWith("data:image")) cb.right.bgImage = "";
      }
      return cb;
    });

  const saveCurrentTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Template name cannot be empty.");
      return;
    }
    try {
      const newTemplate = {
        name: templateName,
        blocks: JSON.stringify(cleanBlocksForStorage(blocks)),
        globalSettings: JSON.stringify(globalSettings),
        createdAt: new Date().toISOString(),
      };
      const docRef = await addDoc(
        collection(db, "emailTemplates"),
        newTemplate,
      );
      setSavedTemplates([...savedTemplates, { id: docRef.id, ...newTemplate }]);
      setLoadedTemplateId(docRef.id);
      toast.success("Template architecture saved.");
    } catch (error) {
      toast.error("Failed to commit template to database.");
    }
  };

  const updateCurrentTemplate = async () => {
    if (!loadedTemplateId || !templateName.trim()) {
      toast.error("Template name cannot be empty.");
      return;
    }
    try {
      const updatedData = {
        name: templateName,
        blocks: JSON.stringify(cleanBlocksForStorage(blocks)),
        globalSettings: JSON.stringify(globalSettings),
        updatedAt: new Date().toISOString(),
      };
      await updateDoc(doc(db, "emailTemplates", loadedTemplateId), updatedData);
      setSavedTemplates(
        savedTemplates.map((t) =>
          t.id === loadedTemplateId ? { ...t, ...updatedData } : t,
        ),
      );
      toast.success("Template successfully updated.");
    } catch (error) {
      toast.error("Failed to update template.");
    }
  };

  const loadTemplate = (templateId) => {
    if (!templateId) {
      setLoadedTemplateId(null);
      setTemplateName("");
      return;
    }
    const tpl = savedTemplates.find((t) => t.id === templateId);
    if (tpl) {
      setBlocks(
        typeof tpl.blocks === "string" ? JSON.parse(tpl.blocks) : tpl.blocks,
      );
      setGlobalSettings(
        typeof tpl.globalSettings === "string"
          ? JSON.parse(tpl.globalSettings)
          : tpl.globalSettings,
      );
      setTemplateName(tpl.name);
      setLoadedTemplateId(tpl.id);
      toast.success(`${tpl.name} loaded into the builder.`);
    }
  };

  const handleImageUpload = (e, blockId, side = null, isBackground = false) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Embedded image payload must be less than 2MB.");
      return;
    }
    const uploadTrackerId = `${blockId}-${side || "main"}-${isBackground ? "bg" : "src"}`;
    setUploadingBlockId(uploadTrackerId);
    const reader = new FileReader();
    reader.onloadend = () => {
      const targetKey = isBackground ? "bgImage" : "src";
      if (side) {
        updateColumnContent(blockId, side, targetKey, reader.result);
      } else {
        updateBlock(blockId, targetKey, reader.result);
      }
      toast.success("Image asset embedded.");
      setUploadingBlockId(null);
      e.target.value = null;
    };
    reader.onerror = () => {
      toast.error("Failed to encode image data.");
      setUploadingBlockId(null);
      e.target.value = null;
    };
    reader.readAsDataURL(file);
  };

  const updateGlobalSetting = (key, value) =>
    setGlobalSettings({ ...globalSettings, [key]: value });

  const addBlock = (type) => {
    if (type === "button") {
      const buttonCount = blocks.filter(
        (b) =>
          b.type === "button" ||
          (b.type === "split" &&
            (b.left.type === "button" || b.right.type === "button")),
      ).length;
      if (buttonCount >= 5) {
        toast.error("You can only add up to 5 buttons per email.");
        return;
      }
    }
    const newBlock = {
      id: Date.now(),
      type,
      align: "left",
      paddingTop: 16,
      paddingBottom: 16,
      useBgImage: false,
      bgImage: "",
      fallbackBgColor: "transparent",
      marginTop: 0,
      marginBottom: 0,
      marginLeft: 0,
      marginRight: 0,
      innerPaddingTop: 0,
      innerPaddingBottom: 0,
      innerPaddingLeft: 0,
      innerPaddingRight: 0,
    };
    if (type === "heading")
      Object.assign(newBlock, {
        text: "New Heading",
        color: globalSettings.headingColor,
        fontSize: 24,
        isBold: true,
      });
    if (type === "text")
      Object.assign(newBlock, {
        text: "Enter your text here...",
        color: globalSettings.textColor,
        fontSize: 15,
      });
    if (type === "image")
      Object.assign(newBlock, {
        src: "",
        width: 0,
        isHero: false,
        align: "center",
        paddingTop: 0,
        paddingBottom: 0,
      });
    if (type === "button")
      Object.assign(newBlock, {
        text: "Click Here",
        url: "https://",
        align: "center",
        bgColor: "#a855f7",
        color: "#ffffff",
        fontSize: 16,
        isBold: true,
        useGradient: false,
        gradientStart: "#a855f7",
        gradientEnd: "#4f46e5",
      });
    if (type === "social")
      Object.assign(newBlock, { socials: [], iconSize: 24, align: "center" });
    if (type === "split")
      Object.assign(newBlock, {
        left: {
          type: "text",
          text: "Left column text",
          align: "left",
          color: globalSettings.textColor,
          fontSize: 15,
          marginTop: 0,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
          innerPaddingTop: 0,
          innerPaddingBottom: 0,
          innerPaddingLeft: 0,
          innerPaddingRight: 0,
        },
        right: {
          type: "text",
          text: "Right column text",
          align: "left",
          color: globalSettings.textColor,
          fontSize: 15,
          marginTop: 0,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
          innerPaddingTop: 0,
          innerPaddingBottom: 0,
          innerPaddingLeft: 0,
          innerPaddingRight: 0,
        },
      });
    setBlocks([...blocks, newBlock]);
  };

  const updateBlock = (id, key, value) =>
    setBlocks(blocks.map((b) => (b.id === id ? { ...b, [key]: value } : b)));
  const updateColumnContent = (blockId, side, key, value) =>
    setBlocks(
      blocks.map((b) =>
        b.id === blockId ? { ...b, [side]: { ...b[side], [key]: value } } : b,
      ),
    );

  const changeColumnType = (blockId, side, newType) => {
    setBlocks(
      blocks.map((block) => {
        if (block.id !== blockId) return block;
        const newContent = {
          type: newType,
          align: ["image", "button", "social"].includes(newType)
            ? "center"
            : "left",
          marginTop: 0,
          marginBottom: 0,
          marginLeft: 0,
          marginRight: 0,
          innerPaddingTop: 0,
          innerPaddingBottom: 0,
          innerPaddingLeft: 0,
          innerPaddingRight: 0,
        };
        if (newType === "heading")
          Object.assign(newContent, {
            text: "Heading",
            color: globalSettings.headingColor,
            fontSize: 24,
            isBold: true,
          });
        if (newType === "text")
          Object.assign(newContent, {
            text: "Text content...",
            color: globalSettings.textColor,
            fontSize: 15,
          });
        if (newType === "image")
          Object.assign(newContent, { src: "", width: 0, isHero: false });
        if (newType === "social")
          Object.assign(newContent, { socials: [], iconSize: 24 });
        if (newType === "button")
          Object.assign(newContent, {
            text: "Click Here",
            url: "https://",
            bgColor: "#a855f7",
            color: "#ffffff",
            fontSize: 16,
            isBold: true,
            useGradient: false,
            gradientStart: "#a855f7",
            gradientEnd: "#4f46e5",
          });
        return { ...block, [side]: newContent };
      }),
    );
  };

  const removeBlock = (id) => setBlocks(blocks.filter((b) => b.id !== id));

  const moveBlock = (index, direction) => {
    const newBlocks = [...blocks];
    if (direction === "up" && index > 0)
      [newBlocks[index - 1], newBlocks[index]] = [
        newBlocks[index],
        newBlocks[index - 1],
      ];
    else if (direction === "down" && index < newBlocks.length - 1)
      [newBlocks[index + 1], newBlocks[index]] = [
        newBlocks[index],
        newBlocks[index + 1],
      ];
    setBlocks(newBlocks);
  };

  const sendEmail = (event) => {
    event.preventDefault();
    if (!notificationTitle.length) {
      toast.error("Subject Title cannot be empty");
      return;
    }
    if (builderMode === "basic" && !notificationText.length) {
      toast.error("Email body cannot be empty in Basic Mode.");
      return;
    }
    if (
      builderMode === "advanced" &&
      (!liveHtmlPreview || liveHtmlPreview.trim() === "")
    ) {
      toast.error("Email body failed to render in Advanced Mode.");
      return;
    }
    let selectedUsers = users
      .filter((u) => selectFromMS.some((item) => item.value === u.value))
      .map((u) => ({ id: u.value, email: u.email }));
    selectedUsers = [...selectedUsers, ...extraEmail];
    if (!selectedUsers.length) {
      toast.error("Please select at least one recipient");
      return;
    }
    setLoading(true);
    const payload = {
      subject: notificationTitle,
      to: selectedUsers,
      app: appName,
    };
    if (builderMode === "basic") payload.body = notificationText;
    else payload.htmlBody = liveHtmlPreview;
    fetch(base_url + "/auth/sendDirectEmail", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (res.ok) {
          toast.success("Email sent successfully");
          setNotificationTitle("");
          if (builderMode === "basic") setNotificationText("");
        } else {
          toast.error("Failed to send Email");
        }
        return res.json();
      })
      .finally(() => setLoading(false));
  };

  const renderBackgroundControls = (blockId, blockData) => {
    if (blockData.type === "image") return null;
    return (
      <div className="flex flex-col gap-3 p-3 bg-slate-100 rounded border border-slate-200 mt-3">
        <label className="flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer">
          <input
            type="checkbox"
            checked={blockData.useBgImage || false}
            onChange={(e) =>
              updateBlock(blockId, "useBgImage", e.target.checked)
            }
            className="rounded text-indigo-600"
          />
          Overlay Element on Background Image
        </label>
        {blockData.useBgImage && (
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={blockData.bgImage || ""}
                onChange={(e) =>
                  updateBlock(blockId, "bgImage", e.target.value)
                }
                placeholder="Background URL..."
                className="flex-1 w-full outline-none px-3 py-1.5 text-xs rounded border border-slate-300"
              />
              <label
                className={`flex items-center justify-center px-3 py-1.5 rounded border border-slate-300 text-[10px] font-bold uppercase cursor-pointer ${uploadingBlockId === `${blockId}-main-bg` ? "bg-slate-200 text-slate-400" : "bg-white text-indigo-700"}`}
              >
                {uploadingBlockId === `${blockId}-main-bg` ? "..." : "Upload"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={uploadingBlockId === `${blockId}-main-bg`}
                  onChange={(e) => handleImageUpload(e, blockId, null, true)}
                />
              </label>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={blockData.fallbackBgColor || "transparent"}
                onChange={(e) =>
                  updateBlock(blockId, "fallbackBgColor", e.target.value)
                }
                className="w-5 h-5 border-0 p-0 rounded cursor-pointer"
              />
              <span className="text-[10px] text-slate-500 uppercase font-bold">
                Fallback Solid Color
              </span>
            </div>
          </div>
        )}
        {!blockData.useBgImage && (
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={blockData.fallbackBgColor || "transparent"}
              onChange={(e) =>
                updateBlock(blockId, "fallbackBgColor", e.target.value)
              }
              className="w-5 h-5 border-0 p-0 rounded cursor-pointer"
            />
            <span className="text-[10px] text-slate-500 uppercase font-bold">
              Container Background Color
            </span>
          </div>
        )}
      </div>
    );
  };

  const renderSpacingControls = (content, onChangeHandler) => (
    <div className="flex flex-col gap-2 mt-2 p-2 bg-slate-50 border border-slate-200 rounded">
      <div className="text-[10px] font-bold text-slate-500 uppercase">
        Margin Matrix (Px)
      </div>
      <div className="grid grid-cols-4 gap-2">
        {["marginTop", "marginRight", "marginBottom", "marginLeft"].map(
          (key, i) => (
            <div key={key} className="flex flex-col">
              <span className="text-[9px] text-slate-400 text-center mb-0.5">
                {["TOP", "RIGHT", "BOTTOM", "LEFT"][i]}
              </span>
              <input
                type="number"
                value={content[key] || 0}
                onChange={(e) =>
                  onChangeHandler(key, parseInt(e.target.value) || 0)
                }
                className="w-full outline-none px-1 py-1 text-xs text-center rounded border border-slate-300"
              />
            </div>
          ),
        )}
      </div>
      <div className="text-[10px] font-bold text-slate-500 uppercase mt-2">
        Padding Matrix (Px)
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[
          "innerPaddingTop",
          "innerPaddingRight",
          "innerPaddingBottom",
          "innerPaddingLeft",
        ].map((key, i) => (
          <div key={key} className="flex flex-col">
            <span className="text-[9px] text-slate-400 text-center mb-0.5">
              {["TOP", "RIGHT", "BOTTOM", "LEFT"][i]}
            </span>
            <input
              type="number"
              value={content[key] || 0}
              onChange={(e) =>
                onChangeHandler(key, parseInt(e.target.value) || 0)
              }
              className="w-full outline-none px-1 py-1 text-xs text-center rounded border border-slate-300"
            />
          </div>
        ))}
      </div>
    </div>
  );

  const renderControlInputs = (
    content,
    onChangeHandler,
    blockId,
    side = null,
  ) => {
    if (content.type === "empty")
      return (
        <div className="text-sm text-slate-400 italic py-4 text-center">
          Empty Column
        </div>
      );
    return (
      <div className="flex flex-col gap-2 mt-2">
        {(content.type === "heading" || content.type === "text") && (
          <>
            <textarea
              rows={content.type === "heading" ? "1" : "3"}
              value={content.text}
              onChange={(e) => onChangeHandler("text", e.target.value)}
              className="w-full outline-none px-3 py-2 text-sm rounded-md text-slate-900 bg-white border border-slate-200 resize-none"
            />
            <div className="flex justify-between items-center gap-2">
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={content.color}
                  onChange={(e) => onChangeHandler("color", e.target.value)}
                  className="w-6 h-6 border-0 p-0 rounded cursor-pointer"
                />
                <span className="text-[10px] text-slate-500 uppercase font-bold">
                  Color
                </span>
              </div>
              <div className="flex items-center gap-1 border border-slate-200 rounded p-0.5 bg-white">
                <button
                  onClick={() => onChangeHandler("isBold", !content.isBold)}
                  className={`w-6 h-6 rounded text-xs font-bold ${content.isBold ? "bg-indigo-100 text-indigo-700" : "text-slate-600"}`}
                >
                  B
                </button>
                <button
                  onClick={() => onChangeHandler("isItalic", !content.isItalic)}
                  className={`w-6 h-6 rounded text-xs italic ${content.isItalic ? "bg-indigo-100 text-indigo-700" : "text-slate-600"}`}
                >
                  I
                </button>
                <button
                  onClick={() =>
                    onChangeHandler("isUnderline", !content.isUnderline)
                  }
                  className={`w-6 h-6 rounded text-xs underline ${content.isUnderline ? "bg-indigo-100 text-indigo-700" : "text-slate-600"}`}
                >
                  U
                </button>
              </div>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={
                    content.fontSize || (content.type === "heading" ? 24 : 15)
                  }
                  onChange={(e) =>
                    onChangeHandler("fontSize", parseInt(e.target.value) || 0)
                  }
                  className="w-12 outline-none px-1 py-1 text-xs rounded border border-slate-200"
                />
                <span className="text-[10px] text-slate-500 font-bold">PX</span>
              </div>
            </div>
            {renderSpacingControls(content, onChangeHandler)}
          </>
        )}
        {content.type === "social" && (
          <div className="flex flex-col gap-3 mt-2 p-2 bg-slate-50 rounded border border-slate-200">
            <select
              value=""
              onChange={(e) => {
                if (!e.target.value) return;
                const platform = SOCIAL_PLATFORMS.find(
                  (p) => p.value === e.target.value,
                );
                onChangeHandler("socials", [
                  ...(content.socials || []),
                  {
                    id: Date.now(),
                    platform: platform.value,
                    url: "",
                    iconUrl: platform.defaultIcon,
                  },
                ]);
              }}
              className="w-full outline-none px-3 py-2 text-sm rounded border border-slate-300"
            >
              <option value="">+ Select Platform to Add...</option>
              {SOCIAL_PLATFORMS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
            {content.socials && content.socials.length > 0 && (
              <div className="flex flex-col gap-2">
                {content.socials.map((social, sIndex) => (
                  <div
                    key={social.id}
                    className="flex flex-wrap items-center gap-2 bg-white p-2 rounded border border-slate-200"
                  >
                    <img
                      src={social.iconUrl}
                      alt="icon"
                      className="w-6 h-6 bg-slate-800 rounded p-1 flex-shrink-0"
                    />
                    <input
                      type="text"
                      value={social.url}
                      onChange={(e) => {
                        const s = [...content.socials];
                        s[sIndex].url = e.target.value;
                        onChangeHandler("socials", s);
                      }}
                      placeholder="Platform URL"
                      className="flex-1 min-w-[80px] outline-none px-2 py-1 text-xs rounded border border-slate-300"
                    />
                    <input
                      type="text"
                      value={social.iconUrl}
                      onChange={(e) => {
                        const s = [...content.socials];
                        s[sIndex].iconUrl = e.target.value;
                        onChangeHandler("socials", s);
                      }}
                      placeholder="Icon URL"
                      className="flex-1 min-w-[80px] outline-none px-2 py-1 text-xs rounded border border-slate-300"
                    />
                    <button
                      onClick={() =>
                        onChangeHandler(
                          "socials",
                          content.socials.filter((s) => s.id !== social.id),
                        )
                      }
                      className="p-1 text-red-500 hover:bg-red-50 rounded flex-shrink-0"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 mt-2 pt-3 border-t border-slate-200">
              <span className="text-[10px] text-slate-500 font-bold uppercase">
                Icon Size (PX)
              </span>
              <input
                type="number"
                value={content.iconSize || 24}
                onChange={(e) =>
                  onChangeHandler("iconSize", parseInt(e.target.value) || 0)
                }
                className="w-16 outline-none px-2 py-1.5 text-sm rounded border border-slate-300"
              />
            </div>
            {renderSpacingControls(content, onChangeHandler)}
          </div>
        )}
        {content.type === "image" && (
          <>
            <div className="flex gap-2">
              <input
                type="text"
                value={content.src}
                placeholder="Image URL..."
                onChange={(e) => onChangeHandler("src", e.target.value)}
                className="flex-1 w-full outline-none px-3 py-2 text-sm rounded-md text-slate-900 bg-white border border-slate-200"
              />
              <label
                className={`flex items-center justify-center px-3 py-2 rounded-md border border-slate-200 text-xs font-bold uppercase cursor-pointer ${uploadingBlockId === `${blockId}-${side || "main"}-src` ? "bg-slate-100 text-slate-400" : "bg-indigo-50 text-indigo-700"}`}
              >
                {uploadingBlockId === `${blockId}-${side || "main"}-src`
                  ? "..."
                  : "Embed"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={
                    uploadingBlockId === `${blockId}-${side || "main"}-src`
                  }
                  onChange={(e) => handleImageUpload(e, blockId, side)}
                />
              </label>
            </div>
            <div className="flex flex-col gap-2 mt-2 pt-2 border-t border-slate-100">
              <span className="text-[10px] text-slate-500 font-bold uppercase">
                Image Width
              </span>
              <div className="flex gap-2">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    onChangeHandler("width", 150);
                  }}
                  className={`px-2 py-1 text-[10px] uppercase font-bold rounded ${content.width === 150 ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500"}`}
                >
                  Small
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    onChangeHandler("width", 300);
                  }}
                  className={`px-2 py-1 text-[10px] uppercase font-bold rounded ${content.width === 300 ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500"}`}
                >
                  Medium
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    onChangeHandler("width", 600);
                  }}
                  className={`px-2 py-1 text-[10px] uppercase font-bold rounded ${content.width === 600 ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500"}`}
                >
                  Large
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-500 font-bold uppercase">
                  Custom (px, 0 = auto)
                </span>
                <input
                  type="number"
                  value={content.width || 0}
                  onChange={(e) =>
                    onChangeHandler("width", parseInt(e.target.value) || 0)
                  }
                  className="w-20 outline-none px-2 py-1 text-sm rounded border border-slate-300"
                />
              </div>
            </div>
            {renderSpacingControls(content, onChangeHandler)}
          </>
        )}
        {content.type === "button" && (
          <>
            <input
              type="text"
              value={content.text}
              placeholder="Button Text"
              onChange={(e) => onChangeHandler("text", e.target.value)}
              className="w-full outline-none px-3 py-2 text-sm rounded-md text-slate-900 bg-white border border-slate-200"
            />
            <input
              type="text"
              value={content.url}
              placeholder="URL"
              onChange={(e) => onChangeHandler("url", e.target.value)}
              className="w-full outline-none px-3 py-2 text-sm rounded-md text-slate-900 bg-white border border-slate-200"
            />
            <div className="flex flex-col gap-2 p-2 bg-slate-100 rounded border border-slate-200">
              <label className="flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={content.useGradient || false}
                  onChange={(e) =>
                    onChangeHandler("useGradient", e.target.checked)
                  }
                />
                Use Gradient Background
              </label>
              {content.useGradient ? (
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-1">
                    <input
                      type="color"
                      value={content.gradientStart || "#a855f7"}
                      onChange={(e) =>
                        onChangeHandler("gradientStart", e.target.value)
                      }
                      className="w-5 h-5 border-0 p-0 rounded cursor-pointer"
                    />
                    <span className="text-[10px] text-slate-500 uppercase font-bold">
                      Start
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <input
                      type="color"
                      value={content.gradientEnd || "#4f46e5"}
                      onChange={(e) =>
                        onChangeHandler("gradientEnd", e.target.value)
                      }
                      className="w-5 h-5 border-0 p-0 rounded cursor-pointer"
                    />
                    <span className="text-[10px] text-slate-500 uppercase font-bold">
                      End
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={content.bgColor}
                    onChange={(e) => onChangeHandler("bgColor", e.target.value)}
                    className="w-6 h-6 border-0 p-0 rounded cursor-pointer"
                  />
                  <span className="text-xs text-slate-500 uppercase font-bold">
                    Solid Background
                  </span>
                </div>
              )}
              <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={content.color}
                    onChange={(e) => onChangeHandler("color", e.target.value)}
                    className="w-6 h-6 border-0 p-0 rounded cursor-pointer"
                  />
                  <span className="text-xs text-slate-500 uppercase font-bold">
                    Text
                  </span>
                </div>
                <div className="flex items-center gap-1 border border-slate-200 rounded p-0.5 bg-white">
                  <button
                    onClick={() => onChangeHandler("isBold", !content.isBold)}
                    className={`w-5 h-5 rounded text-[10px] font-bold ${content.isBold ? "bg-indigo-100 text-indigo-700" : "text-slate-600"}`}
                  >
                    B
                  </button>
                  <button
                    onClick={() =>
                      onChangeHandler("isItalic", !content.isItalic)
                    }
                    className={`w-5 h-5 rounded text-[10px] italic ${content.isItalic ? "bg-indigo-100 text-indigo-700" : "text-slate-600"}`}
                  >
                    I
                  </button>
                </div>
              </div>
            </div>
            {renderSpacingControls(content, onChangeHandler)}
          </>
        )}
        {content.type !== "divider" && (
          <div className="flex gap-1 mt-1">
            {["left", "center", "right"].map((align) => (
              <button
                key={align}
                onClick={() => onChangeHandler("align", align)}
                className={`px-2 py-1 text-[10px] uppercase font-bold rounded ${content.align === align ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-500"}`}
              >
                {align}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      style={{
        flex: 1,
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        padding: "24px 16px",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <ToastContainer className="text-[1rem]" />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "24px",
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: "clamp(1.25rem, 4vw, 2rem)",
            fontWeight: 700,
            color: "#0f172a",
          }}
        >
          Campaign Builder
        </h1>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "white",
            padding: "4px",
            borderRadius: "10px",
            border: "1px solid #e2e8f0",
          }}
        >
          {["basic", "advanced"].map((mode) => (
            <button
              key={mode}
              onClick={() => setBuilderMode(mode)}
              style={{
                padding: "8px 16px",
                fontSize: "14px",
                fontWeight: 600,
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                backgroundColor:
                  builderMode === mode ? "#4f46e5" : "transparent",
                color: builderMode === mode ? "white" : "#475569",
              }}
            >
              {mode === "basic" ? "Basic Mode" : "Advanced Mode"}
            </button>
          ))}
        </div>
      </div>
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          padding: "24px",
          marginBottom: "24px",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label
              style={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
              }}
            >
              Subject Line
            </label>
            <input
              type="text"
              placeholder={`e.g., Welcome to ${appName}`}
              value={notificationTitle}
              onChange={handleNotificationTitle}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                backgroundColor: "#f8fafc",
                fontSize: "16px",
                color: "#0f172a",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label
              style={{
                fontSize: "12px",
                fontWeight: 700,
                color: "#64748b",
                textTransform: "uppercase",
              }}
            >
              Select Recipients
            </label>
            <div style={{ color: "#0f172a" }}>
              <MultiSelect
                className="text-slate-900"
                valueRenderer={(selected) =>
                  selected.length
                    ? selected.map(({ label }) => label).join(", ")
                    : "No Users Selected"
                }
                options={users}
                value={selectFromMS}
                onChange={setselectFromMS}
                labelledBy="Search and select users..."
              />
            </div>
            <MultipleEmailForm
              onDelete={removeEmail}
              Emails={extraEmail}
              onAdd={AddEmail}
            />
          </div>
        </div>
      </div>
      {builderMode === "basic" && (
        <div
          style={{
            backgroundColor: "white",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            padding: "24px",
            marginBottom: "24px",
          }}
        >
          <label
            style={{
              fontSize: "12px",
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              display: "block",
              marginBottom: "8px",
            }}
          >
            Email Text Content
          </label>
          <textarea
            style={{
              width: "100%",
              height: "200px",
              padding: "16px",
              borderRadius: "8px",
              border: "1px solid #cbd5e1",
              backgroundColor: "#f8fafc",
              fontSize: "16px",
              color: "#0f172a",
              outline: "none",
              resize: "vertical",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
            placeholder="Write your basic notification here..."
            value={notificationText}
            onChange={handleNotificationTextChange}
          />
        </div>
      )}
      {builderMode === "advanced" && (
        <>
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              border: "1px solid #e2e8f0",
              padding: "24px",
              marginBottom: "24px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                flexWrap: "wrap",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#1e293b",
                }}
              >
                Global Canvas Settings
              </h2>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <select
                  onChange={(e) => loadTemplate(e.target.value)}
                  value={loadedTemplateId || ""}
                  style={{
                    padding: "8px 12px",
                    fontSize: "14px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    outline: "none",
                  }}
                >
                  <option value="">Load Saved Template...</option>
                  {savedTemplates.map((tpl) => (
                    <option key={tpl.id} value={tpl.id}>
                      {tpl.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Template Name"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  style={{
                    padding: "8px 12px",
                    fontSize: "14px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    outline: "none",
                    width: "140px",
                  }}
                />
                {loadedTemplateId && (
                  <button
                    onClick={updateCurrentTemplate}
                    style={{
                      backgroundColor: "#4f46e5",
                      color: "white",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      border: "none",
                      fontSize: "14px",
                      fontWeight: 700,
                      cursor: "pointer",
                    }}
                  >
                    Update
                  </button>
                )}
                <button
                  onClick={saveCurrentTemplate}
                  style={{
                    backgroundColor: "#eef2ff",
                    color: "#4f46e5",
                    border: "1px solid #c7d2fe",
                    padding: "8px 16px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Save New
                </button>
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: "20px",
                borderTop: "1px solid #f1f5f9",
                paddingTop: "16px",
              }}
            >
              {[
                { label: "Page Background", key: "pageColor" },
                { label: "Container Box", key: "containerColor" },
                { label: "Borders & Dividers", key: "borderColor" },
              ].map(({ label, key }) => (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  <label
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#94a3b8",
                      textTransform: "uppercase",
                    }}
                  >
                    {label}
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <input
                      type="color"
                      value={globalSettings[key]}
                      onChange={(e) => updateGlobalSetting(key, e.target.value)}
                      style={{
                        width: "40px",
                        height: "40px",
                        border: "none",
                        padding: 0,
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "12px",
                        fontFamily: "monospace",
                        color: "#475569",
                      }}
                    >
                      {globalSettings[key]}
                    </span>
                  </div>
                </div>
              ))}
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                  }}
                >
                  Font Family
                </label>
                <select
                  value={globalSettings.fontFamily}
                  onChange={(e) =>
                    updateGlobalSetting("fontFamily", e.target.value)
                  }
                  style={{
                    padding: "8px",
                    borderRadius: "6px",
                    border: "1px solid #cbd5e1",
                    fontSize: "14px",
                    outline: "none",
                  }}
                >
                  <option value="'Inter', sans-serif">Inter</option>
                  <option value="Arial, sans-serif">Arial</option>
                  <option value="'Helvetica Neue', Helvetica, sans-serif">
                    Helvetica
                  </option>
                  <option value="'Times New Roman', Times, serif">
                    Times New Roman
                  </option>
                  <option value="Georgia, serif">Georgia</option>
                </select>
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                <label
                  style={{
                    fontSize: "11px",
                    fontWeight: 700,
                    color: "#94a3b8",
                    textTransform: "uppercase",
                  }}
                >
                  Container Width
                </label>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <input
                    type="number"
                    value={globalSettings.containerWidth}
                    onChange={(e) =>
                      updateGlobalSetting(
                        "containerWidth",
                        parseInt(e.target.value) || 600,
                      )
                    }
                    style={{
                      width: "80px",
                      padding: "8px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "14px",
                      outline: "none",
                    }}
                  />
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#94a3b8",
                      fontWeight: 700,
                    }}
                  >
                    PX
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "24px",
              width: "100%",
            }}
          >
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #e2e8f0",
                  paddingBottom: "8px",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#1e293b",
                  }}
                >
                  Email Architecture
                </h2>
                <span style={{ fontSize: "12px", color: "#94a3b8" }}>
                  {blocks.length} Blocks
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  maxHeight: "650px",
                  overflowY: "auto",
                  paddingRight: "4px",
                }}
              >
                {blocks.map((block, index) => (
                  <div
                    key={block.id}
                    className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:border-indigo-300 transition-colors"
                  >
                    <div className="flex justify-between items-center mb-4 border-b border-slate-100 pb-3">
                      <span className="text-indigo-600 font-bold uppercase text-xs tracking-wider flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                        {block.type === "split" ? "Split Row" : block.type}
                      </span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => moveBlock(index, "up")}
                          disabled={index === 0}
                          className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 15l7-7 7 7"
                            ></path>
                          </svg>
                        </button>
                        <button
                          onClick={() => moveBlock(index, "down")}
                          disabled={index === blocks.length - 1}
                          className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            ></path>
                          </svg>
                        </button>
                        <button
                          onClick={() => removeBlock(block.id)}
                          className="p-1.5 ml-1 rounded-md text-red-400 hover:bg-red-50"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            ></path>
                          </svg>
                        </button>
                      </div>
                    </div>
                    {renderControlInputs(
                      block,
                      (key, val) => updateBlock(block.id, key, val),
                      block.id,
                      null,
                    )}
                    {block.type === "split" && (
                      <div className="flex gap-4 mt-2">
                        <div className="flex-1 bg-slate-50 p-3 rounded-lg border border-slate-200">
                          <div className="text-xs font-bold text-slate-500 uppercase mb-2">
                            Left Column
                          </div>
                          <select
                            value={block.left.type}
                            onChange={(e) =>
                              changeColumnType(block.id, "left", e.target.value)
                            }
                            className="w-full p-1.5 text-sm border border-slate-300 rounded mb-2 bg-white"
                          >
                            {[
                              "text",
                              "heading",
                              "image",
                              "button",
                              "social",
                              "empty",
                            ].map((t) => (
                              <option key={t} value={t}>
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                              </option>
                            ))}
                          </select>
                          {renderControlInputs(
                            block.left,
                            (key, val) =>
                              updateColumnContent(block.id, "left", key, val),
                            block.id,
                            "left",
                          )}
                        </div>
                        <div className="flex-1 bg-slate-50 p-3 rounded-lg border border-slate-200">
                          <div className="text-xs font-bold text-slate-500 uppercase mb-2">
                            Right Column
                          </div>
                          <select
                            value={block.right.type}
                            onChange={(e) =>
                              changeColumnType(
                                block.id,
                                "right",
                                e.target.value,
                              )
                            }
                            className="w-full p-1.5 text-sm border border-slate-300 rounded mb-2 bg-white"
                          >
                            {[
                              "text",
                              "heading",
                              "image",
                              "button",
                              "social",
                              "empty",
                            ].map((t) => (
                              <option key={t} value={t}>
                                {t.charAt(0).toUpperCase() + t.slice(1)}
                              </option>
                            ))}
                          </select>
                          {renderControlInputs(
                            block.right,
                            (key, val) =>
                              updateColumnContent(block.id, "right", key, val),
                            block.id,
                            "right",
                          )}
                        </div>
                      </div>
                    )}
                    {renderBackgroundControls(block.id, block)}
                  </div>
                ))}
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
                  gap: "8px",
                  backgroundColor: "white",
                  padding: "12px",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                }}
              >
                {[
                  { type: "heading", label: "Heading" },
                  { type: "text", label: "Text" },
                  { type: "image", label: "Image" },
                  { type: "button", label: "Button" },
                  { type: "social", label: "Social" },
                  { type: "divider", label: "Divider" },
                ].map(({ type, label }) => (
                  <button
                    key={type}
                    onClick={() => addBlock(type)}
                    style={{
                      padding: "10px 8px",
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#374151",
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    + {label}
                  </button>
                ))}
                <button
                  onClick={() => addBlock("split")}
                  style={{
                    padding: "10px 8px",
                    backgroundColor: "#eef2ff",
                    border: "1px solid #c7d2fe",
                    borderRadius: "8px",
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#4f46e5",
                    cursor: "pointer",
                    gridColumn: "span 2",
                    textAlign: "center",
                  }}
                >
                  + Split Row
                </button>
              </div>
            </div>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "16px" }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #e2e8f0",
                  paddingBottom: "8px",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#1e293b",
                  }}
                >
                  Live Preview
                </h2>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "6px" }}
                >
                  <span
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: "#22c55e",
                      display: "inline-block",
                    }}
                  ></span>
                  <span style={{ fontSize: "12px", color: "#94a3b8" }}>
                    Real-time sync
                  </span>
                </div>
              </div>
              <div
                style={{
                  width: "100%",
                  backgroundColor: "black",
                  borderRadius: "16px",
                  overflow: "hidden",
                  border: "4px solid #1e293b",
                  height: "clamp(400px, 60vw, 750px)",
                  position: "relative",
                }}
              >
                <iframe
                  srcDoc={liveHtmlPreview}
                  title="Email Preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                    position: "absolute",
                    inset: 0,
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: "32px",
          borderTop: "1px solid #e2e8f0",
          paddingTop: "24px",
        }}
      >
        <button
          onClick={sendEmail}
          disabled={isLoading}
          style={{
            padding: "14px 40px",
            borderRadius: "12px",
            fontSize: "17px",
            fontWeight: 600,
            border: "none",
            cursor: isLoading ? "not-allowed" : "pointer",
            backgroundColor: isLoading ? "#cbd5e1" : "#4f46e5",
            color: isLoading ? "#94a3b8" : "white",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            maxWidth: "300px",
            justifyContent: "center",
          }}
        >
          {isLoading ? (
            <>
              <svg
                className="animate-spin h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Dispatching...
            </>
          ) : (
            "Dispatch Campaign"
          )}
        </button>
      </div>
    </div>
  );
};

export default CampaignBuilder;
